import { create } from 'zustand';
import { applyDTODelta, DebugCatalogEntryDTO } from '@hexcrawl/shared-utils';
import { debugApiService } from '../services/debugService';
import { useGameStore } from './gameStore';
import { useLootStore } from './lootStore';

/** Outcome of the last grant, shown in the panel's status line. */
export interface DebugGrantStatus {
  message: string;
  error: boolean;
}

interface DebugStoreState {
  items: DebugCatalogEntryDTO[];
  lootTables: DebugCatalogEntryDTO[];
  catalogLoaded: boolean;
  loadingCatalog: boolean;
  busy: boolean;
  status: DebugGrantStatus | null;
  loadCatalog: (gameId: string) => Promise<void>;
  grantItem: (gameId: string, defId: string) => Promise<void>;
  grantLoot: (gameId: string, tableId: string) => Promise<void>;
  clear: () => void;
}

/**
 * State for the debug cheat panel. The catalog is built from the server's live item and
 * loot registries and never changes within a session, so it is fetched once — the first
 * time the panel is opened — and cached here.
 */
export const useDebugStore = create<DebugStoreState>()((set, get) => ({
  items: [],
  lootTables: [],
  catalogLoaded: false,
  loadingCatalog: false,
  busy: false,
  status: null,

  loadCatalog: async (gameId: string) => {
    if (get().catalogLoaded || get().loadingCatalog) return;
    set({ loadingCatalog: true });
    try {
      const catalog = await debugApiService.fetchCatalog(gameId);
      set({ items: catalog.items, lootTables: catalog.lootTables, catalogLoaded: true });
    } catch (err: any) {
      set({ status: { message: err?.message || 'Failed to load debug catalog.', error: true } });
    } finally {
      set({ loadingCatalog: false });
    }
  },

  grantItem: async (gameId: string, defId: string) => {
    set({ busy: true, status: null });
    try {
      const result = await debugApiService.grantItem(gameId, defId);
      const gameState = useGameStore.getState().gameState;
      if (gameState) {
        useGameStore.getState().setGameState(applyDTODelta(gameState, result.stateDelta));
      }
      set({ status: { message: `Granted ${result.grantedName}.`, error: false } });
    } catch (err: any) {
      set({ status: { message: err?.message || 'Failed to grant item.', error: true } });
    } finally {
      set({ busy: false });
    }
  },

  grantLoot: async (gameId: string, tableId: string) => {
    set({ busy: true, status: null });
    try {
      const result = await debugApiService.grantLoot(gameId, tableId);
      const gameState = useGameStore.getState().gameState;
      if (gameState) {
        useGameStore.getState().setGameState(applyDTODelta(gameState, result.stateDelta));
      }
      // Reviewed and claimed through the ordinary loot UI, not a debug-only display.
      useLootStore.getState().setPendingLoot(result.pendingLoot);
      set({ status: { message: `Rolled ${tableId}.`, error: false } });
    } catch (err: any) {
      set({ status: { message: err?.message || 'Failed to roll loot.', error: true } });
    } finally {
      set({ busy: false });
    }
  },

  clear: () => set({
    items: [],
    lootTables: [],
    catalogLoaded: false,
    loadingCatalog: false,
    busy: false,
    status: null,
  }),
}));
